/**
 * One-approver org — V98-3-4 (research §2 items 15, 16; R4/R5 Sam).
 * Plain language, an honest reporting state ("your auditor decides"), and
 * ONE primary: "Add an advisor seat · clears N" with eligibility guidance.
 *
 * V98-3 review U8 / S6:
 *   - a seat counts only once an ACTIVE member fills it. Until then the
 *     card stays, in its invite state: "Invite pending · <name>" with
 *     Resend and Copy link (the accept link is returned by add / resend,
 *     never stored) — never "sent" unless the email went out.
 *   - the batch compensating review is grouped by kind with a truthful
 *     count: only signatures can be recorded that way (a review of your
 *     own access needs the independent party). Never the primary.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Field, Input } from '@aigovops/design-system';
import type { OwedRow } from '@aigovops/core';
import { addIndependentParty, resendIndependentPartyInvite, type IndependentPartySeatResult, type IndependentPartyView } from '../../lib/coverage-api';
import { fmtDay, itemHref } from '../../lib/today-model';
import { ActionDialog, Chip, errorText } from './shared';

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Rows grouped by what can be done with them (the batch only covers signatures). */
export function soloGroups(rows: readonly OwedRow[]): { signatures: OwedRow[]; others: OwedRow[] } {
  const signatures = rows.filter((r) => r.ref.kind === 'signature_request');
  return { signatures, others: rows.filter((r) => !signatures.includes(r)) };
}

export function SoloCard({ rows, prefix, slug, parties, lastInvite, canManage, onAddAdvisor, onInvite }: {
  rows: readonly OwedRow[]; prefix: string; slug: string;
  /** Seats that exist but no active member fills yet (invited / expired / contact only). */
  parties: readonly IndependentPartyView[];
  /** The latest add / resend result (carries the one-time accept link). */
  lastInvite: IndependentPartySeatResult | null;
  /** Owner/admin: seats are theirs to add (server-enforced); others ask an admin. */
  canManage: boolean;
  onAddAdvisor: (opener: HTMLElement) => void;
  onInvite: (r: IndependentPartySeatResult, msg: string) => void;
}) {
  const qc = useQueryClient();
  const n = rows.length;
  const { signatures } = soloGroups(rows);
  const pending = parties.filter((p) => p.state !== 'active');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // R3-U12: "Copied" belongs to the link that was copied — a resend (new link) or another seat starts fresh.
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const resend = async (p: IndependentPartyView) => {
    setBusy(p.party_id); setError(null);
    try {
      const r = await resendIndependentPartyInvite(slug, p.party_id);
      await qc.invalidateQueries({ queryKey: ['coverage-config', slug] });
      onInvite(r, r.invite?.state === 'sent' ? `Invite sent again to ${p.contact_name}` : `New invite link ready for ${p.contact_name}`);
    } catch (e) { setError(errorText(e, 'The invite could not be resent.')); }
    finally { setBusy(null); }
  };
  const copy = async (url: string) => {
    try { await navigator.clipboard.writeText(url); setCopiedLink(url); } catch { setError('Copy failed — select the link and copy it.'); }
  };
  const link = lastInvite?.invite?.accept_url ?? null;

  return (
    <section className="gp-panel" aria-labelledby="solo-h" id="solo">
      <h2 id="solo-h" tabIndex={-1}>You’re the only approver</h2>
      <p>{`${n} thing${n === 1 ? '' : 's'} you prepared need${n === 1 ? 's' : ''} a second person.`}</p>
      <p className="gp-dim">Common at small companies · your auditor decides how it’s reported.</p>
      {pending.length ? (
        <ul className="gp-plain-list" aria-label="Advisor seats">
          {pending.map((p) => (
            <li key={p.party_id} className="gp-ow" id={`seat-${p.party_id}`} tabIndex={-1}>
              <span className="gp-ow-main">
                <span className="gp-ow-t">{p.contact_name}</span>
                <span className="gp-ow-meta">
                  <Chip text={p.state === 'invited' ? 'Invite pending' : p.state === 'invite_expired' ? 'Invite expired' : 'No invite yet'} tone="amber" />
                  {p.invitation && p.state === 'invited' ? <span>{`expires ${fmtDay(p.invitation.expires_at)}`}</span> : null}
                  {p.contact_email ? <span>{p.contact_email}</span> : null}
                </span>
              </span>
              {canManage ? (
                <span className="gp-ow-act">
                  <Button compact aria-disabled={busy === p.party_id || undefined} aria-label={`Resend invite · ${p.contact_name}`} onClick={() => void resend(p)}>
                    {busy === p.party_id ? 'Sending…' : 'Resend'}
                  </Button>
                  {link && lastInvite?.party_id === p.party_id
                    ? <Button compact aria-label={`Copy invite link · ${p.contact_name}`} onClick={() => void copy(link)}>{copiedLink === link ? 'Copied' : 'Copy link'}</Button>
                    : null}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {error ? <p className="gp-field-err" role="alert">{error}</p> : null}
      <div className="gp-actions">
        {canManage
          ? <Button id="solo-add-advisor" variant={pending.length ? 'secondary' : 'primary'} onClick={(e) => onAddAdvisor(e.currentTarget)}>
              {pending.length ? 'Add another advisor seat' : `Add an advisor seat · clears ${n}`}
            </Button>
          : <span className="gp-dim">An owner or admin adds advisor seats.</span>}
        {signatures.length ? (
          <Link className="gp-linkbtn" to={`${itemHref(prefix, signatures[0]!.ref)}&batch=${encodeURIComponent(signatures.map((r) => r.ref.id).join(','))}`}>
            {signatures.length === 1 ? 'Record it as a compensating review…' : `Record ${signatures.length} signatures as compensating reviews…`}
          </Link>
        ) : null}
      </div>
    </section>
  );
}

/** Add an advisor seat (independent party, rule 16) with eligibility guidance. Owner/admin (server-enforced). */
export function AdvisorDialog({ open, slug, clears, onClose, onDone }: {
  open: boolean; slug: string; clears: number; onClose: () => void; onDone: (msg: string, result: IndependentPartySeatResult) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    const errs: typeof errors = {};
    if (!name.trim()) errs.name = 'Enter their name.';
    if (!EMAIL_RX.test(email.trim())) errs.email = 'Enter a work email, like name@firm.com.';
    setErrors(errs);
    if (errs.name || errs.email) return false;
    setBusy(true); setError(null);
    try {
      const r = await addIndependentParty(slug, { kind: 'advisor_seat', contact_name: name.trim(), contact_email: email.trim() });
      await Promise.all([qc.invalidateQueries({ queryKey: ['coverage-config', slug] }), qc.invalidateQueries({ queryKey: ['today', slug] })]);
      // Truthful (S6): "sent" only when the email went out; otherwise the link is there to copy.
      const msg = r.state === 'active' ? `${name.trim()} fills the advisor seat`
        : r.invite?.state === 'sent' ? `Invite sent to ${name.trim()}` : `Seat added · copy the invite link for ${name.trim()}`;
      onDone(msg, r);
      return true;
    } catch (e) { setError(errorText(e, 'The advisor seat could not be added.')); return false; }
    finally { setBusy(false); }
  };
  return (
    <ActionDialog open={open} title="Add an advisor seat" submitLabel="Add seat" busy={busy} error={error} onClose={onClose} onSubmit={submit}>
      <Field id="advisor-name" label="Name" required error={errors.name}>
        <Input value={name} maxLength={200} onChange={(e) => { setName(e.currentTarget.value); setError(null); if (errors.name) setErrors((x) => ({ ...x, name: undefined })); }} />
      </Field>
      <Field id="advisor-email" label="Work email" required hint="They approve once they accept the invite" error={errors.email}>
        <Input type="email" value={email} maxLength={320} onChange={(e) => { setEmail(e.currentTarget.value); setError(null); if (errors.email) setErrors((x) => ({ ...x, email: undefined })); }} />
      </Field>
      <dl className="gp-dl" aria-label="Who can take the seat">
        <dt>Outside advisor · fractional CISO · board member</dt><dd>Yes</dd>
        <dt>Anyone who reports to you</dt><dd>Never</dd>
        <dt>Anyone at your audit firm</dt><dd>Never</dd>
        <dt>They approve</dt><dd>{`Policies · risk acceptances · your own access (clears ${clears})`}</dd>
        <dt>Anything they prepared</dt><dd>Never</dd>
      </dl>
    </ActionDialog>
  );
}
